import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postV2ProviderEprescriptionsSetupClinic: build.mutation<
      PostV2ProviderEprescriptionsSetupClinicApiResponse,
      PostV2ProviderEprescriptionsSetupClinicApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/eprescriptions/setup-clinic`,
        method: "POST",
        body: queryArg.setupClinicRequest,
      }),
    }),
    postV2ProviderEprescriptionsSetupClinician: build.mutation<
      PostV2ProviderEprescriptionsSetupClinicianApiResponse,
      PostV2ProviderEprescriptionsSetupClinicianApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/eprescriptions/setup-clinician`,
        method: "POST",
        body: queryArg.setupClinicianRequest,
      }),
    }),
    getV2ProviderEprescriptionsSso: build.query<
      GetV2ProviderEprescriptionsSsoApiResponse,
      GetV2ProviderEprescriptionsSsoApiArg
    >({
      query: () => ({ url: `/v2/provider/eprescriptions/sso` }),
    }),
    getV2ProviderEprescriptionsStatus: build.query<
      GetV2ProviderEprescriptionsStatusApiResponse,
      GetV2ProviderEprescriptionsStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/eprescriptions/status`,
        params: {
          patient_id: queryArg.patientId,
        },
      }),
    }),
    postV2ProviderPatientsByPatientIdEprescriptionsConnect: build.mutation<
      PostV2ProviderPatientsByPatientIdEprescriptionsConnectApiResponse,
      PostV2ProviderPatientsByPatientIdEprescriptionsConnectApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/eprescriptions/connect`,
        method: "POST",
        body: queryArg.connectPatientRequest,
      }),
    }),
    getV2ProviderPatientsByPatientIdEprescriptionsSso: build.query<
      GetV2ProviderPatientsByPatientIdEprescriptionsSsoApiResponse,
      GetV2ProviderPatientsByPatientIdEprescriptionsSsoApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/eprescriptions/sso`,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type PostV2ProviderEprescriptionsSetupClinicApiResponse =
  /** status 200 OK */ EprescriptionStatusResponse;
export type PostV2ProviderEprescriptionsSetupClinicApiArg = {
  setupClinicRequest: SetupClinicRequest;
};
export type PostV2ProviderEprescriptionsSetupClinicianApiResponse =
  /** status 200 OK */ EprescriptionStatusResponse;
export type PostV2ProviderEprescriptionsSetupClinicianApiArg = {
  setupClinicianRequest: SetupClinicianRequest;
};
export type GetV2ProviderEprescriptionsSsoApiResponse =
  /** status 200 OK */ ClinicianSsoResponse;
export type GetV2ProviderEprescriptionsSsoApiArg = void;
export type GetV2ProviderEprescriptionsStatusApiResponse =
  /** status 200 OK */ EprescriptionStatusResponse;
export type GetV2ProviderEprescriptionsStatusApiArg = {
  /** Optional patient ID to check patient connection status */
  patientId?: string | null;
};
export type PostV2ProviderPatientsByPatientIdEprescriptionsConnectApiResponse =
  /** status 200 OK */ PatientConnectionResponse;
export type PostV2ProviderPatientsByPatientIdEprescriptionsConnectApiArg = {
  patientId: string;
  connectPatientRequest: ConnectPatientRequest;
};
export type GetV2ProviderPatientsByPatientIdEprescriptionsSsoApiResponse =
  /** status 200 OK */ PatientSsoResponse;
export type GetV2ProviderPatientsByPatientIdEprescriptionsSsoApiArg = {
  patientId: string;
};
export type EprescriptionStatusResponse = {
  /** Whether the organization has a DoseSpot clinic */
  organization_connected: boolean;
  /** Whether the patient is connected to DoseSpot (only present if patient_id provided) */
  patient_connected?: boolean | null;
  /** Whether the provider is registered as a DoseSpot clinician */
  user_connected: boolean;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type DoseSpotClinic = {
  /** Street address */
  address_line1: string;
  /** City */
  city: string;
  /** Clinic name */
  clinic_name: string;
  /** Primary phone number. Accepts formats like 555-123-4567, (555) 123-4567, or 1-555-123-4567. Non-digit characters are stripped and US country code (1) is removed. Must be exactly 10 digits after normalization. */
  phone: string;
  /** State abbreviation (e.g., MA) */
  state: string;
  /** ZIP code */
  zip_code: string;
};
export type SetupClinicRequest = {
  /** Clinic data for organization setup */
  clinic: DoseSpotClinic;
};
export type DoseSpotClinician = {
  /** Street address */
  address: string;
  /** City */
  city: string;
  /** DEA Registration Number for prescribers (used for controlled substances). Format: two letters followed by seven digits (e.g., AB1234567). */
  dea_number?: string | null;
  /** Date of birth (YYYY-MM-DD) */
  dob: string;
  /** Email address */
  email: string;
  /** Set to true to indicate the clinician has requested EPCS (Electronic Prescribing for Controlled Substances). */
  epcs_requested?: boolean | null;
  /** Fax number. Accepts formats like 555-123-4567, (555) 123-4567, or 1-555-123-4567. Non-digit characters are stripped and US country code (1) is removed. Must be exactly 10 digits after normalization. */
  fax: string;
  /** First name */
  first_name: string;
  /** Last name */
  last_name: string;
  /** Individual NPI number */
  npi: string;
  /** Phone number. Accepts formats like 555-123-4567, (555) 123-4567, or 1-555-123-4567. Non-digit characters are stripped and US country code (1) is removed. Must be exactly 10 digits after normalization. */
  phone: string;
  /** State abbreviation (e.g., MA) */
  state: string;
  /** ZIP code */
  zip_code: string;
};
export type SetupClinicianRequest = {
  /** Clinician data for provider registration */
  clinician: DoseSpotClinician;
};
export type ClinicianSsoResponse = {
  /** URL to open DoseSpot clinician onboarding UI */
  sso_url: string;
};
export type PatientConnectionResponse = {
  /** Whether the patient is now connected to DoseSpot */
  connected: boolean;
  /** The patient ID */
  patient_id: string;
};
export type DoseSpotPatient = {
  /** Street address */
  address: string;
  /** City */
  city: string;
  /** Date of birth (YYYY-MM-DD) */
  dob: string;
  /** First name */
  first_name: string;
  /** Gender: 'male', 'female', or 'unknown'. Maps to DoseSpot values: Male=1, Female=2, Unknown=3. */
  gender: string;
  /** Last name */
  last_name: string;
  /** Phone number. Accepts formats like 555-123-4567, (555) 123-4567, or 1-555-123-4567. Non-digit characters are stripped and US country code (1) is removed. Must be exactly 10 digits after normalization. */
  phone: string;
  /** State abbreviation (e.g., MA) */
  state: string;
  /** ZIP code */
  zip_code: string;
};
export type ConnectPatientRequest = {
  /** Patient data for DoseSpot connection */
  patient: DoseSpotPatient;
};
export type PatientSsoResponse = {
  /** URL to open DoseSpot prescribing UI for this patient */
  sso_url: string;
};
